/**
 * Save a flow's current answer as a playlist. The flow itself is unchanged and
 * keeps curating; the playlist is a snapshot of what it chose.
 */

import { useState } from "react";
import { commitExport } from "./flowApi";
import { clock, type FlowPick } from "./flowMath";

export function FlowExport({
  picks,
  defaultName,
  onClose,
  onDone,
}: {
  picks: FlowPick[];
  defaultName: string;
  onClose: () => void;
  onDone: (playlistId: number) => void;
}) {
  const [name, setName] = useState(defaultName);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const seconds = picks.reduce((s, p) => s + p.air, 0);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await commitExport(name.trim() || "Flow", picks.map((p) => p.trackId), Math.round(seconds));
      if (res.error || !res.playlistId) {
        setError(res.error ?? "The playlist was not created.");
        return;
      }
      onDone(res.playlistId);
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fs-modal" role="dialog" aria-label="Save as playlist" onClick={onClose}>
      <div className="fs-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="ws-panel-title">Save as playlist</div>
        <label className="fs-export-name">
          Playlist name <input autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        {error && <div className="write-result bad">{error}</div>}
        <div className="fs-dialog-actions">
          <span className="ws-dim">{picks.length} songs · about {clock(seconds)}</span>
          <span className="ws-spacer" />
          <button className="ghost" onClick={onClose}>Cancel</button>
          <button className="tool-button" disabled={busy || !picks.length} onClick={() => void create()}>
            {busy ? "Saving…" : "Save playlist"}
          </button>
        </div>
      </div>
    </div>
  );
}
